import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, ListGroup, Badge, Form } from 'react-bootstrap';

const sanitizarSerie = (s) => String(s || '')
    .replace(/[\r\n\t]/g, ' ')
    .replace(/[<>&"']/g, '')
    .slice(0, 60);

/**
 * ModalIncluirDependienteCert — al marcar el padre en columna "Certificado" del acta
 * pregunta si incluir también al(los) dependiente(s). Ofrece checkbox para recordar
 * la decisión para las demás parejas del acta.
 *
 * Spec: openspec/changes/items-adicionales-equipo-dependiente — capability
 * `actas-entrega-parejas-dependientes`.
 *
 * Props:
 *   show        boolean
 *   padre       EquipmentInOt del padre
 *   hijos       EquipmentInOt[] de los hijos entregables
 *   onIncluir   (recordar: boolean) => void — incluir hijos + guardar decisión si `recordar`
 *   onSoloPadre (recordar: boolean) => void — dejar solo padre + guardar decisión si `recordar`
 *   onCancelar  () => void
 */
const ModalIncluirDependienteCert = ({
    show,
    padre,
    hijos = [],
    onIncluir,
    onSoloPadre,
    onCancelar,
}) => {
    const [recordar, setRecordar] = useState(false);

    // Reset "recordar" cada vez que se abre el modal — evita fugas entre diálogos.
    useEffect(() => {
        if (show) setRecordar(false);
    }, [show]);

    return (
        <Modal show={show} onHide={onCancelar} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton={false}>
                <Modal.Title>Incluir dependiente(s) en el certificado</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    El equipo marcado tiene <strong>{hijos.length} dependiente(s)</strong> con certificado disponible en esta acta.
                    ¿También deseas incluirlos en el acta?
                </Alert>

                <div className="mb-3">
                    <strong>Padre:</strong>{' '}
                    {padre?.snapshot?.item || 'Item'}
                    {' — Serie '}
                    {sanitizarSerie(padre?.snapshot?.serial || padre?.equipo?.serie)}
                </div>

                <div className="ms-3">
                    <strong>Dependiente(s):</strong>
                    <ListGroup className="mt-1">
                        {hijos.map((h) => (
                            <ListGroup.Item key={h._id}>
                                <Badge bg="info" className="me-2">🔗</Badge>
                                {h.snapshot?.item || h.equipo?.item?.nombre || 'Item'}
                                {' — Serie '}
                                {sanitizarSerie(h.snapshot?.serial || h.equipo?.serie)}
                                {h.consecutivo && (
                                    <Badge bg="secondary" className="ms-2">
                                        Consec. {h.consecutivo}
                                    </Badge>
                                )}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>

                <Form.Check
                    className="mt-3"
                    type="checkbox"
                    id="recordar-decision-pareja-cert"
                    label="Recordar decisión para las demás parejas de esta acta"
                    checked={recordar}
                    onChange={(e) => setRecordar(e.target.checked)}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCancelar}>
                    Cancelar
                </Button>
                <Button variant="outline-warning" onClick={() => onSoloPadre(recordar)}>
                    Solo el padre
                </Button>
                <Button variant="primary" onClick={() => onIncluir(recordar)} autoFocus>
                    Incluir dependiente(s) (recomendado)
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalIncluirDependienteCert;
